/**
 * D39 -- Home widget: My performance tasks (pending self + manager reviews).
 * Auto-hides when nothing pending. Sits next to <MyChecklistTasksWidget>.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, ArrowRight, UserCheck, Users } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import GlassSurface from "@/components/ui/GlassSurface";

export default function MyPerformanceTasksWidget() {
  const [data, setData] = useState({ self: [], manager: [], hr: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/performance/my-tasks")
       .then((r) => setData(r.data || { self: [], manager: [], hr: [] }))
       .catch(() => setData({ self: [], manager: [], hr: [] }))
       .finally(() => setLoading(false));
  }, []);

  const totalCount = (data.self?.length || 0) + (data.manager?.length || 0) + (data.hr?.length || 0);
  if (loading) return null;
  if (totalCount === 0) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                 data-testid="home-perf-tasks">
      <GlassSurface variant="card" className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-primary flex items-center justify-center">
            <Target className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-foreground/55">
              My performance tasks
            </div>
            <div className="text-[11px] text-foreground/45">
              {totalCount} pending across self / manager / HR
            </div>
          </div>
          <Link to="/profile#performance"
                  className="ml-auto text-xs text-sky-300 hover:text-sky-200 flex items-center gap-1"
                  data-testid="home-perf-see-all">
            Open <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {data.self?.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-foreground/50 flex items-center gap-1.5">
              <UserCheck className="h-3 w-3" /> Self-review pending ({data.self.length})
            </div>
            {data.self.slice(0, 3).map((r) => (
              <Link key={r.id} to="/profile#performance"
                      data-testid={`home-perf-self-${r.id}`}
                      className="flex items-center gap-2 p-2 rounded-md bg-white/5 hover:bg-white/10 transition text-xs">
                <span className="truncate flex-1">{r.cycle_name}</span>
                {r.auto_generated && (
                  <Badge variant="outline" className="text-[9px] border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200">
                    Auto probation
                  </Badge>
                )}
              </Link>
            ))}
          </div>
        )}

        {data.manager?.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-foreground/50 flex items-center gap-1.5">
              <Users className="h-3 w-3" /> Manager review pending ({data.manager.length})
            </div>
            {data.manager.slice(0, 3).map((r) => (
              <Link key={r.id} to="/hr/performance"
                      data-testid={`home-perf-manager-${r.id}`}
                      className="flex items-center gap-2 p-2 rounded-md bg-white/5 hover:bg-white/10 transition text-xs">
                <span className="truncate flex-1">{r.employee_name} · {r.cycle_name}</span>
              </Link>
            ))}
          </div>
        )}

        {data.hr?.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-foreground/50">
              HR moderation queue ({data.hr.length})
            </div>
            {data.hr.slice(0, 3).map((r) => (
              <Link key={r.id} to="/hr/performance"
                      className="flex items-center gap-2 p-2 rounded-md bg-white/5 hover:bg-white/10 transition text-xs"
                      data-testid={`home-perf-hr-${r.id}`}>
                <span className="truncate flex-1">{r.employee_name} · {r.cycle_name}</span>
              </Link>
            ))}
          </div>
        )}
      </GlassSurface>
    </motion.div>
  );
}
